import React from 'react';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import {
  UPSERT_INCOME_FORM_ID,
  UpsertIncomeFormProps,
} from '../../components/UpsertIncomeModal/UpsertIncomeModal.meta';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useSocialSecurityForm } from './useSocialSecurityForm';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { useEstimateMonthlyPIAMutation } from '../../api/useEstimateMonthlyPIAMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../CommonConstants';
import { receivingBenefitsOptions } from './SocialSecurityForm.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';
import { SocialSecurityEmpty } from '../../tables/SocialSecurity/SocialSecurity.empty';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { getIncomeOwnerModel } from '../../Income.meta';

export type Props = UpsertIncomeFormProps;

export const SocialSecurityForm = (props: Props) => {
  const { clientId, workflowProps } = props;

  const { Dob: primaryDob } = useSelector(selectCurrentClient);

  if (!primaryDob) {
    return <SocialSecurityEmpty clientId={clientId} workflowProps={workflowProps} />;
  }

  return <SocialSecurityFormContent {...props} />;
};

export const SocialSecurityFormContent = ({
  clientId,
  income,
  data,
  onSuccess,
  readOnly,
}: Props) => {
  const {
    data: {
      formBag: { control, setValue, watch, resetField },
      mutationBag,
      earnerOptions,
      isSpouse,
      incomeFrequencyOptions,
      defaultIncomeFrequency,
      futureAgeOptions,
      pastAgeOptions,
      showCurrentlyReceivingBenefits,
      managementStyleValue,
      options,
    },
    actions: { onSubmit },
  } = useSocialSecurityForm({ clientId, income, data, onSuccess, readOnly });

  const currentlyReceivingBenefits = watch('currentlyReceivingBenefits');
  const currentlyReceivingBenefitsValue = watch('currentlyReceivingBenefits');
  const earnerValue = watch('earner');
  const startAge = watch('startAge');

  const ageOptions = currentlyReceivingBenefits ? pastAgeOptions : futureAgeOptions;

  const {
    mutateAsync: estimateMonthlyPIA,
    isLoading: isEstimatingMonthlyPIA,
  } = useEstimateMonthlyPIAMutation();

  const handleEstimateMonthlyPIA = async () => {
    if (readOnly) {
      return;
    }

    try {
      const { EstimateMonthlyPIA } = await estimateMonthlyPIA({
        clientId,
        isSpouse,
        hypotheticalStartAge: startAge || undefined,
      });

      setValue('amount', EstimateMonthlyPIA);
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_INCOME_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
      aria-label="Social security income form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Controller
        name="earner"
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label="Earner"
            value={earnerOptions
              .map(({ options }) => options)
              .flat()
              .find(({ value: v }) => v === value)}
            options={earnerOptions}
            onChange={option => {
              onChange(option?.value);
              resetField('startAge', { defaultValue: null });
              if (option) {
                setValue(
                  'ownerModel',
                  getIncomeOwnerModel({ options: options.OwnerOptions, value: option.value }),
                );
              }
            }}
            isPortal
          />
        )}
      />

      {showCurrentlyReceivingBenefits && (
        <Controller
          name="currentlyReceivingBenefits"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              label="Currently Receiving Benefits?"
              value={receivingBenefitsOptions.find(option => option.value === value)}
              options={receivingBenefitsOptions}
              onChange={option => {
                onChange(option?.value);
                resetField('startAge', { defaultValue: null });
              }}
              isPortal
            />
          )}
        />
      )}

      <Box>
        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value, ...field } }) => (
            <InputNumberMask
              {...field}
              label={currentlyReceivingBenefits ? 'Current Benefit Amount' : 'Monthly PIA'}
              value={value}
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...(currentlyReceivingBenefits
                ? {
                    selectorProps: {
                      defaultValue:
                        incomeFrequencyOptions.find(
                          ({ value: v }) => v === defaultIncomeFrequency,
                        ) || incomeFrequencyOptions[1],
                      options: incomeFrequencyOptions,
                      onChange: option => setValue('incomeFrequency', option.value),
                      ariaLabel: 'Income frequency selector',
                    },
                  }
                : {
                    innerText: '/month',
                    tooltip:
                      'The Primary Insurance Amount (PIA) is the benefit a person would receive if he/she elects to begin receiving retirement benefits at his/her Full Retirement Age (FRA). Estimates are also available from the Social Security Administration at https://www.ssa.gov/.',
                  })}
            />
          )}
        />

        {!currentlyReceivingBenefits && (
          <Button
            variant="link"
            leftIcon="fas fa-magic"
            disabled={isEstimatingMonthlyPIA || readOnly}
            onClick={handleEstimateMonthlyPIA}
            mt="8"
          >
            Estimate
          </Button>
        )}
      </Box>

      <Controller
        name="startAge"
        control={control}
        key={`${earnerValue}-${currentlyReceivingBenefitsValue}`}
        render={({ field: { value, onChange, ...field } }) => {
          const selectedValue = ageOptions.find(option => option.value === value);

          return (
            <Select
              {...field}
              label={currentlyReceivingBenefits ? 'Age at Filing' : 'Starts at Age'}
              value={selectedValue}
              options={ageOptions}
              onChange={option => {
                if (option?.value === 'FRA') {
                  setValue('useFra', true);
                } else {
                  setValue('useFra', null);
                }
                onChange(option?.value);
              }}
              tooltip={
                currentlyReceivingBenefits
                  ? 'Select the age you filed for social security benefits. If left blank, it will be assumed that your filing age is your current age.'
                  : 'Age will be used to calculate monthly retirement benefit based on monthly PIA.'
              }
              isPortal
            />
          );
        }}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName="orionManagementStyleId"
        onChange={(value: number | null) => {
          setValue('customBucketIndex', value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName="customBucketIndex"
        managementStyleValue={managementStyleValue}
        options={options}
      />
    </form>
  );
};
